import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Mail, Phone, Building2, Truck, Hash } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const FLEET_TYPES = [
  "Property (general freight)",
  "Reefer / refrigerated",
  "Flatbed",
  "Tanker",
  "HazMat",
  "Auto haul",
  "Passenger",
  "Other",
];

export default function Register() {
  const [form, setForm] = useState({
    name: "",
    company_name: "",
    email: "",
    phone: "",
    mc_number: "",
    dot_number: "",
    number_of_trucks: "",
    fleet_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name, email, and phone.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        email: form.email.trim().toLowerCase(),
        number_of_trucks: form.number_of_trucks ? parseInt(form.number_of_trucks, 10) : null,
      };
      const { data } = await api.post("/leads", payload);
      setSubmitted(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white text-slate-900 p-6">
        <div className="w-full max-w-xl hs-fade-up text-center" data-testid="register-success">
          <div className="hs-card p-10">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
              <CheckCircle2 size={36} className="text-emerald-600" />
            </div>
            <h1 className="font-display font-black text-3xl uppercase tracking-tighter">Thanks, {form.name.split(" ")[0]}!</h1>
            <div className="hs-stripe mt-4 mb-6" />
            <p className="text-slate-700">
              Got it — <strong>our sales team will reach out within one business day</strong> to walk you
              through the platform and get your trucking operation onboarded.
            </p>
            <p className="text-sm text-slate-500 mt-6">
              Most teams hear back from us in a few hours. If you don&rsquo;t hear back by tomorrow, email{" "}
              <a href="mailto:support@crystallogistics.services" className="text-blue-700 hover:underline">
                support@crystallogistics.services
              </a>
              .
            </p>
            {submitted.duplicate && (
              <p className="text-xs text-amber-600 mt-3 bg-amber-50 border-l-2 border-amber-500 p-2 text-left">
                We already received an inquiry from this email in the last 24 hours — we&rsquo;ll be in touch.
                No need to resubmit.
              </p>
            )}
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link to="/" className="hs-btn-secondary">BACK TO HOME</Link>
              <Link to="/login" className="hs-btn-primary inline-flex items-center gap-2" data-testid="register-success-login">
                ALREADY A CLIENT? SIGN IN <ArrowRight size={14} />
              </Link>
            </div>
          </div>
          <p className="text-[11px] text-slate-400 mt-6">
            By submitting, you agree to be contacted by Crystal Logistics Services about our compliance SaaS.
            We never share your info.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-white text-slate-900 p-6">
      <form onSubmit={onSubmit} className="w-full max-w-2xl hs-fade-up" data-testid="register-form">
        <div className="flex items-center mb-8">
          <img src="/logo.png" alt="Crystal Logistics Services" className="h-24 w-auto" />
        </div>
        <div className="hs-stripe" />
        <h1 className="font-display font-black text-4xl uppercase tracking-tighter mt-6">Create Account</h1>
        <p className="text-slate-600 mt-3 max-w-lg">
          Tell us about your trucking operation. Our sales team will reach out within one business day
          to walk you through Crystal Logistics&rsquo; compliance SaaS and set up your account.
        </p>

        <div className="hs-card p-6 mt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="hs-overline mb-1 block">Your Name *</label>
              <input value={form.name} onChange={set("name")} required className="hs-input"
                     placeholder="Jane Doe" data-testid="register-name" />
            </div>

            <div>
              <label className="hs-overline mb-1 block flex items-center gap-1">
                <Building2 size={11} /> Company Name
              </label>
              <input value={form.company_name} onChange={set("company_name")} className="hs-input"
                     placeholder="Acme Trucking LLC" data-testid="register-company" />
            </div>

            <div>
              <label className="hs-overline mb-1 block flex items-center gap-1">
                <Truck size={11} /> Number of Trucks
              </label>
              <input value={form.number_of_trucks} onChange={set("number_of_trucks")} type="number"
                     min="0" className="hs-input font-mono" placeholder="5" data-testid="register-trucks" />
            </div>

            <div>
              <label className="hs-overline mb-1 block flex items-center gap-1">
                <Mail size={11} /> Work Email *
              </label>
              <input value={form.email} onChange={set("email")} type="email" required
                     className="hs-input" placeholder="you@company.com" data-testid="register-email" />
            </div>

            <div>
              <label className="hs-overline mb-1 block flex items-center gap-1">
                <Phone size={11} /> Phone *
              </label>
              <input value={form.phone} onChange={set("phone")} type="tel" required
                     className="hs-input font-mono" placeholder="+1 (555) 010-0123" data-testid="register-phone" />
            </div>

            <div>
              <label className="hs-overline mb-1 block flex items-center gap-1">
                <Hash size={11} /> MC Number
              </label>
              <input value={form.mc_number} onChange={set("mc_number")} className="hs-input font-mono"
                     placeholder="MC-123456" data-testid="register-mc" />
            </div>

            <div>
              <label className="hs-overline mb-1 block flex items-center gap-1">
                <Hash size={11} /> USDOT Number
              </label>
              <input value={form.dot_number} onChange={set("dot_number")} className="hs-input font-mono"
                     placeholder="1234567" data-testid="register-dot" />
            </div>

            <div className="md:col-span-2">
              <label className="hs-overline mb-1 block">Fleet Type</label>
              <select value={form.fleet_type} onChange={set("fleet_type")}
                      className="hs-input" data-testid="register-fleet-type">
                <option value="">Select one (optional)</option>
                {FLEET_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="hs-overline mb-1 block">Anything else our team should know?</label>
              <textarea value={form.message} onChange={set("message")} className="hs-input min-h-[80px]"
                        placeholder="Pain points with current compliance setup, timeline to switch, etc."
                        data-testid="register-message" />
            </div>
          </div>

          <button type="submit" disabled={loading}
                  className="hs-btn-primary inline-flex items-center gap-2 mt-6 w-full justify-center"
                  data-testid="register-submit">
            {loading ? "SUBMITTING…" : "CREATE ACCOUNT"}
            {!loading && <ArrowRight size={14} />}
          </button>

          <p className="text-[11px] text-slate-400 mt-4 text-center">
            We only use your info to contact you about our service. No spam, no list-selling.
          </p>
        </div>

        <div className="text-center mt-6">
          <span className="text-sm text-slate-500">Already have an account? </span>
          <Link to="/login" className="text-blue-700 hover:underline font-semibold" data-testid="register-existing-login">
            Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
